/**
 *  @class
 *  @function FreeShipping
 */

if (!customElements.get('free-shipping')) {
  // Static flag to ensure only one instance processes free gift at a time
  let globalFreeGiftProcessing = false;

  class FreeShipping extends HTMLElement {
    constructor() {
      super();
      this.isProcessing = false;
      this.boundHandleCartChange = this.handleCartChange.bind(this);
    }

    connectedCallback() {
      this.update();

      // Listen for all cart-related events globally
      const events = [
        'line-item:change:end', // Cart quantity changes
        'cart:updated', // General cart updates
        'cart:refresh', // Cart refresh
        'product:added-to-cart', // Product added from product page
        'cart:item-added', // Item added to cart
      ];

      events.forEach(event => {
        document.addEventListener(event, this.boundHandleCartChange);
      });
    }

    async handleCartChange(event) {
      if (this.isProcessing || globalFreeGiftProcessing) {
        return;
      }

      // Set processing flags immediately to prevent duplicate calls
      this.isProcessing = true;
      globalFreeGiftProcessing = true;

      try {
        // Minimal delay for cart state to settle
        await new Promise(resolve => setTimeout(resolve, 50));

        // Fetch latest cart and update
        const response = await fetch('/cart.js');
        const cart = await response.json();

        this.dataset.cartTotal = cart.total_price;
        this.update();

        // Handle free gift logic (only if this is the first component instance)
        await this.checkAndUpdateFreeGift(cart);
      } catch (error) {
        console.error('Error handling cart change:', error);
      } finally {
        // Minimal delay to prevent rapid re-triggers
        await new Promise(resolve => setTimeout(resolve, 100));
        this.isProcessing = false;
        globalFreeGiftProcessing = false;
      }
    }

    update() {
      const total = parseInt(this.dataset.cartTotal, 10);
      const shippingMinimum =
        Math.round(
          parseInt(this.dataset.minimum, 10) * (Shopify.currency.rate || 1)
        ) || 0;

      // Parse gift milestones from JSON data attribute
      let giftMilestonesData = [];
      try {
        const giftMilestonesJson = this.dataset.giftMilestones;
        if (giftMilestonesJson) {
          giftMilestonesData = JSON.parse(giftMilestonesJson);
        }
      } catch (error) {
        console.error('Error parsing gift milestones:', error);
      }

      // Collect active milestones (threshold > 0)
      const shippingMilestone = this.querySelector(
        '.free-shipping--milestone-shipping'
      );
      const giftMilestoneElements = this.querySelectorAll(
        '.free-shipping--milestone-gift'
      );

      const milestones = [];

      // Add shipping milestone if enabled
      if (shippingMinimum > 0 && shippingMilestone) {
        milestones.push({
          element: shippingMilestone,
          threshold: shippingMinimum,
          type: 'shipping',
        });
        // Update shipping milestone label with converted price
        this.updateMilestoneLabel(
          shippingMilestone,
          shippingMinimum,
          'Free Shipping'
        );
      }

      // Add gift milestones
      giftMilestoneElements.forEach((element, index) => {
        const thresholdAttr = parseInt(element.dataset.threshold, 10);
        if (thresholdAttr > 0 && giftMilestonesData[index]) {
          // Convert gift threshold using currency rate
          const convertedThreshold = Math.round(
            thresholdAttr * (Shopify.currency.rate || 1)
          );

          milestones.push({
            element: element,
            threshold: convertedThreshold,
            type: 'gift',
            variantIds: element.dataset.variantIds
              ? element.dataset.variantIds.split(',')
              : [],
            giftIndex: index + 1,
          });

          // Update gift milestone label with converted price
          const giftLabels = [
            '2 Free Gifts',
            '3rd Free Gifts',
            '4th Free Gifts',
          ];
          this.updateMilestoneLabel(
            element,
            convertedThreshold,
            giftLabels[index] || `Free Gift ${index + 1}`
          );
        }
      });

      // Exit early if no milestones
      if (milestones.length === 0) {
        return;
      }

      // Sort by threshold value (lowest first)
      milestones.sort((a, b) => a.threshold - b.threshold);

      // Update the title text based on achieved milestones
      this.updateTitleText(total, milestones);

      // Calculate progress based on visual milestone positions
      let percentage = 0;
      const totalMilestones = milestones.length;

      // Find which milestone range we're in and calculate proportional progress
      for (let i = 0; i < milestones.length; i++) {
        if (total >= milestones[i].threshold) {
          // We've passed this milestone
          percentage = (i + 1) / totalMilestones;
        } else {
          // We're between this milestone and the previous one
          const prevThreshold = i > 0 ? milestones[i - 1].threshold : 0;
          const prevPosition = i > 0 ? i / totalMilestones : 0;
          const currentPosition = (i + 1) / totalMilestones;

          // Calculate proportional progress between milestones
          const progressInRange =
            (total - prevThreshold) / (milestones[i].threshold - prevThreshold);
          const visualRange = currentPosition - prevPosition;

          percentage = prevPosition + progressInRange * visualRange;
          break;
        }
      }

      // Cap at 100%
      percentage = Math.min(percentage, 1);

      // Update progress bar
      this.style.setProperty('--percentage', percentage);

      // Position milestones evenly and highlight if achieved
      milestones.forEach((milestone, index) => {
        if (milestone.element) {
          // Calculate even spacing
          const position = ((index + 1) / totalMilestones) * 100;
          milestone.element.style.left = `${position}%`;

          // Highlight if threshold achieved
          milestone.element.classList.toggle(
            'achieved',
            total >= milestone.threshold
          );
        }
      });
    }

    updateMilestoneLabel(milestoneElement, amountInCents, labelText) {
      const labelElement = milestoneElement.querySelector(
        '.free-shipping--milestone-label'
      );
      if (!labelElement) {
        return;
      }

      const formattedPrice = this.formatPrice(amountInCents);

      // Update the label content
      labelElement.innerHTML = `
        <span>${labelText}</span>
        <strong>${formattedPrice}</strong>
      `;
    }

    updateTitleText(total, milestones) {
      const titleElement = this.querySelector('.free-shipping--title');
      if (!titleElement) {
        return;
      }

      // Separate shipping and gift milestones
      const shippingMilestone = milestones.find(m => m.type === 'shipping');
      const giftMilestones = milestones.filter(m => m.type === 'gift');

      const hasShipping = !!shippingMilestone;
      const shippingAchieved =
        hasShipping && total >= shippingMilestone.threshold;

      // Find achieved and pending gifts
      const achievedGifts = giftMilestones.filter(m => total >= m.threshold);
      const pendingGifts = giftMilestones.filter(m => total < m.threshold);

      let titleHTML = '';

      // All milestones achieved
      if (
        (!hasShipping || shippingAchieved) &&
        achievedGifts.length === giftMilestones.length &&
        giftMilestones.length >= 2
      ) {
        titleHTML = `<strong>Free Shipping & 3 Free Gifts unlocked!</strong>`;
      }
      // Free shipping + first gift achieved, pending second gift
      else if (
        shippingAchieved &&
        achievedGifts.length === 1 &&
        pendingGifts.length > 0
      ) {
        const nextGift = pendingGifts[0];
        const remaining = nextGift.threshold - total;
        const formattedRemaining = this.formatPrice(remaining);
        titleHTML = `Free Shipping & Free Gift unlocked!<br><strong>Spend ${formattedRemaining} more for a 3rd Free Gift</strong>`;
      }
      // Free shipping achieved, pending first gift
      else if (
        shippingAchieved &&
        achievedGifts.length === 0 &&
        giftMilestones.length > 0
      ) {
        const firstGift = giftMilestones[0];
        const remaining = firstGift.threshold - total;
        const formattedRemaining = this.formatPrice(remaining);
        titleHTML = `Free Shipping unlocked!<br><strong>Spend ${formattedRemaining} more for 2 Free Gifts</strong>`;
      }
      // Free shipping achieved, all gifts achieved
      else if (
        shippingAchieved &&
        achievedGifts.length === giftMilestones.length &&
        giftMilestones.length > 0
      ) {
        const totalGifts = achievedGifts.length === 1 ? '2' : '3';
        titleHTML = `<strong>Free Shipping & ${totalGifts} Free Gifts unlocked!</strong>`;
      }
      // Nothing achieved yet - show next milestone (shipping)
      else if (!shippingAchieved && hasShipping) {
        const remaining = shippingMilestone.threshold - total;
        const formattedRemaining = this.formatPrice(remaining);
        titleHTML = `<strong>Spend ${formattedRemaining} more for Free Shipping</strong>`;
      }
      // Edge case: no shipping, show first gift
      else if (!hasShipping && giftMilestones.length > 0) {
        const nextGift =
          giftMilestones.find(m => total < m.threshold) || giftMilestones[0];
        const remaining = nextGift.threshold - total;
        const formattedRemaining = this.formatPrice(remaining);
        titleHTML = `<strong>Spend ${formattedRemaining} more for 2 Free Gifts</strong>`;
      }

      titleElement.innerHTML = titleHTML;
    }

    formatPrice(amountInCents) {
      // Format the price using Shopify's formatMoney function
      let formattedPrice = '';
      if (
        typeof formatMoney === 'function' &&
        window.theme &&
        window.theme.settings
      ) {
        formattedPrice = formatMoney(
          amountInCents,
          window.theme.settings.money_with_currency_format
        );
      } else {
        // Fallback to basic formatting
        const symbol = Shopify.currency.active || '$';
        formattedPrice = symbol + Math.round(amountInCents / 100);
      }

      // Remove trailing zeros (.00, ,00, etc.)
      formattedPrice = formattedPrice.replace(/([.,])00$/, '');

      return formattedPrice;
    }

    async checkAndUpdateFreeGift(cart) {
      try {
        // Parse gift milestones from JSON data attribute
        let giftMilestonesData = [];
        try {
          const giftMilestonesJson = this.dataset.giftMilestones;
          if (!giftMilestonesJson) {
            return;
          }
          giftMilestonesData = JSON.parse(giftMilestonesJson);
        } catch (error) {
          console.error('Error parsing gift milestones:', error);
          return;
        }

        if (giftMilestonesData.length === 0) {
          return;
        }

        const cartTotal = cart.total_price;

        // Process each gift milestone
        for (const milestone of giftMilestonesData) {
          const threshold = parseInt(milestone.threshold, 10);
          const variantIds = milestone.variant_ids || [];

          if (!threshold || variantIds.length === 0) {
            continue;
          }

          // Convert threshold using currency rate
          const convertedThreshold = Math.round(
            threshold * (Shopify.currency.rate || 1)
          );

          // Process each variant ID in this milestone
          for (const variantId of variantIds) {
            const variantIdStr = variantId.toString().trim();

            if (!variantIdStr) {
              continue;
            }

            // Find items in cart with this variant ID
            const giftItems = cart.items.filter(
              item =>
                item.variant_id.toString() === variantIdStr &&
                item.properties &&
                item.properties._free_gift === 'true'
            );
            const giftCount = giftItems.length;
            const totalGiftQty = giftItems.reduce(
              (sum, item) => sum + item.quantity,
              0
            );

            // Add free gift if threshold met and none in cart
            if (cartTotal >= convertedThreshold && giftCount === 0) {
              await this.addFreeGift(variantIdStr);
            }
            // Remove free gift(s) if threshold not met and any are in cart
            else if (cartTotal < convertedThreshold && giftCount > 0) {
              // Remove all instances of this free gift
              for (const item of giftItems) {
                await this.removeFreeGift(cart, variantIdStr);
              }
            }
            // Remove extra gifts if more than 1 exist
            else if (giftCount > 1 || totalGiftQty > 1) {
              // Remove all but one
              for (let i = 1; i < giftItems.length; i++) {
                await this.removeFreeGift(cart, variantIdStr);
              }
              // If total qty > 1, update the first item to qty 1
              if (totalGiftQty > 1 && giftItems.length > 0) {
                await this.updateFreeGiftQuantity(cart, variantIdStr, 1);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error checking free gift:', error);
      }
    }

    async addFreeGift(variantId) {
      try {
        const formData = {
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _free_gift: 'true',
              },
            },
          ],
        };

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          // Trigger UI refresh without waiting (fire and forget for speed)
          this.refreshCartUI();
        }
      } catch (error) {
        console.error('Error adding free gift:', error);
      }
    }

    async removeFreeGift(cart, variantId) {
      try {
        const freeGiftItem = cart.items.find(
          item =>
            item.variant_id.toString() === variantId &&
            item.properties &&
            item.properties._free_gift === 'true'
        );

        if (!freeGiftItem) {
          return;
        }

        const lineNumber = cart.items.indexOf(freeGiftItem) + 1;

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: lineNumber,
            quantity: 0,
          }),
        });

        if (response.ok) {
          // Trigger UI refresh without waiting (fire and forget for speed)
          this.refreshCartUI();
        }
      } catch (error) {
        console.error('Error removing free gift:', error);
      }
    }

    async updateFreeGiftQuantity(cart, variantId, quantity) {
      try {
        const freeGiftItem = cart.items.find(
          item =>
            item.variant_id.toString() === variantId &&
            item.properties &&
            item.properties._free_gift === 'true'
        );

        if (!freeGiftItem) {
          return;
        }

        const lineNumber = cart.items.indexOf(freeGiftItem) + 1;

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: lineNumber,
            quantity: quantity,
          }),
        });

        if (response.ok) {
          // Trigger UI refresh without waiting (fire and forget for speed)
          this.refreshCartUI();
        }
      } catch (error) {
        console.error('Error updating free gift quantity:', error);
      }
    }

    async refreshCartUI() {
      // The global processing flag prevents re-triggering, so we don't need to remove listeners

      // Trigger refreshes (they will fetch cart data themselves)
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        cartDrawer.refresh();
      }

      const cartPage = document.getElementById('Cart');
      if (cartPage) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }

      // Update cart count bubble optimistically (cart will be re-fetched by drawer/page)
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Update progress bar immediately
        this.dataset.cartTotal = cart.total_price;
        this.update();

        // Update cart count
        const itemCount = cart.item_count;
        const bubbles = document.querySelectorAll('.thb-item-count');
        bubbles.forEach(bubble => {
          bubble.textContent = itemCount;
          bubble.classList.toggle('hide', itemCount === 0);
        });
      } catch (error) {
        console.error('Error updating cart UI:', error);
      }
    }
  }

  customElements.define('free-shipping', FreeShipping);
}
